import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, Send, ShieldAlert, MessageSquare, CheckCircle2, Archive, Users, Shield } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const STATUS_CFG = {
  open: { label: 'Open', cls: 'bg-[#FEF2F2] text-[#B91C1C] border-[#B91C1C]/20', icon: MessageSquare },
  in_progress: { label: 'In progress', cls: 'bg-[#FDF7EC] text-[#B38138] border-[#B38138]/20', icon: Loader2 },
  resolved: { label: 'Resolved', cls: 'bg-[#15803D]/[0.08] text-[#15803D] border-[#15803D]/20', icon: CheckCircle2 },
  closed: { label: 'Closed', cls: 'bg-black/[0.05] text-[#4B4B52] border-black/10', icon: Archive },
};

function relativeTime(iso) {
  if (!iso) return '';
  const sec = Math.floor((new Date() - new Date(iso)) / 1000);
  if (sec < 60) return 'now';
  if (sec < 3600) return `${Math.floor(sec/60)}m`;
  if (sec < 86400) return `${Math.floor(sec/3600)}h`;
  if (sec < 604800) return `${Math.floor(sec/86400)}d`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function AdminSupport() {
  const navigate = useNavigate();
  const [authChecked, setAuthChecked] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState('open');
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // ticket being viewed/replied
  const [reply, setReply] = useState('');
  const [acting, setActing] = useState(false);

  // Auth gate
  useEffect(() => {
    api.get('/admin/me')
      .then(() => setAuthChecked(true))
      .catch(() => { toast.error('Admin access required'); navigate('/discover', { replace: true }); });
  }, [navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = filter === 'all' ? '' : `?status=${filter}`;
      const r = await api.get(`/admin/tickets${params}`);
      setTickets(r.data.tickets);
      setCounts(r.data.counts || {});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { if (authChecked) load(); }, [authChecked, load]);

  const updateTicket = async (patch, refresh = true) => {
    if (!active) return;
    setActing(true);
    try {
      const r = await api.put(`/admin/tickets/${active.ticket_id}`, patch);
      setActive(r.data);
      if (patch.admin_reply) setReply('');
      toast.success(patch.admin_reply ? 'Reply sent' : 'Updated');
      if (refresh) load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally {
      setActing(false);
    }
  };

  if (!authChecked) {
    return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-7 h-7 animate-spin text-[#B91C1C]"/></div>;
  }

  return (
    <div className="min-h-screen bg-white pb-12" data-testid="admin-support-page">
      <div className="px-5 pt-8 pb-4 flex items-center gap-3 sticky top-0 bg-white/95 backdrop-blur-md z-20 border-b border-[var(--hairline)]">
        <BackNavButton />
        <h1 className="font-brand text-[26px] font-[600] tracking-tight">Support tickets</h1>
        <Link to="/admin/users" className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#4B4B52] hover:text-[#B91C1C] transition" data-testid="link-admin-users">
          <Users size={11}/> Users
        </Link>
        <Link to="/admin/moderation" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#4B4B52] hover:text-[#B91C1C] transition" data-testid="link-admin-moderation">
          <Shield size={11}/> Moderation
        </Link>
        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#B91C1C]">
          <ShieldAlert size={11}/> Admin
        </span>
      </div>

      {/* Filter pills */}
      <div className="px-5 pt-4 pb-2 flex gap-2 overflow-x-auto no-scrollbar">
        {[
          ['open', 'Open'],
          ['in_progress', 'In progress'],
          ['resolved', 'Resolved'],
          ['closed', 'Closed'],
          ['all', 'All'],
        ].map(([k, label]) => {
          const count = k === 'all'
            ? Object.values(counts).reduce((s, n) => s + n, 0)
            : counts[k] ?? 0;
          const sel = filter === k;
          return (
            <button key={k} onClick={() => setFilter(k)} data-testid={`admin-filter-${k}`}
              className={`flex-shrink-0 px-3 py-1.5 rounded-full text-[12px] font-semibold border transition inline-flex items-center gap-1.5 ${
                sel ? 'brand-gradient text-white border-transparent' : 'bg-white border-[var(--hairline-strong)] text-[#4B4B52] hover:bg-black/[0.02]'
              }`}>
              {label}
              <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sel ? 'bg-white/25 text-white' : 'bg-black/[0.05] text-[#4B4B52]'}`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center h-[40vh]"><Loader2 className="w-6 h-6 animate-spin text-[#B91C1C]"/></div>
      ) : tickets.length === 0 ? (
        <div className="flex items-center justify-center h-[40vh] text-center px-6">
          <div>
            <CheckCircle2 className="w-10 h-10 text-[#15803D] mx-auto mb-3" strokeWidth={1.5}/>
            <h2 className="font-display text-lg font-[600]">All clear</h2>
            <p className="text-[#8A8A93] text-sm mt-1">No tickets in this state.</p>
          </div>
        </div>
      ) : (
        <div className="px-3 mt-2 divide-y divide-[var(--hairline)]" data-testid="admin-tickets-list">
          {tickets.map(t => {
            const cfg = STATUS_CFG[t.status] || STATUS_CFG.open;
            return (
              <button key={t.ticket_id} onClick={() => { setActive(t); setReply(''); }}
                data-testid={`admin-ticket-${t.ticket_id}`}
                className="w-full text-left px-3 py-3 flex items-start gap-3 hover:bg-black/[0.02] transition">
                <div className={`badge flex-shrink-0 ${cfg.cls}`}>{cfg.label}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-[14px] text-[#0F0F10] truncate">{t.subject}</p>
                  <p className="text-[12px] text-[#8A8A93] mt-0.5 truncate">{t.message}</p>
                  <div className="flex items-center gap-2 mt-1.5 text-[10px] text-[#8A8A93]">
                    <span className="font-medium">{t.user_name || t.user_email}</span>
                    <span>·</span>
                    <span className="uppercase tracking-wide">{t.category}</span>
                    <span>·</span>
                    <span>{relativeTime(t.created_at)}</span>
                    {(t.replies?.length || 0) > 0 && (
                      <>
                        <span>·</span>
                        <span className="text-[#B91C1C] font-semibold">{t.replies.length} repl{t.replies.length === 1 ? 'y' : 'ies'}</span>
                      </>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail drawer */}
      {active && (
        <div className="fixed inset-0 z-40 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => setActive(null)}>
          <div className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl max-h-[92vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="admin-ticket-drawer">
            <div className="sticky top-0 bg-white px-5 pt-5 pb-3 border-b border-[var(--hairline)] flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap mb-2">
                  <span className={`badge ${STATUS_CFG[active.status]?.cls || ''}`}>{STATUS_CFG[active.status]?.label}</span>
                  <span className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">{active.category}</span>
                  <span className="text-[10px] text-[#8A8A93]">{relativeTime(active.created_at)}</span>
                </div>
                <h2 className="font-display text-lg font-[600] leading-tight">{active.subject}</h2>
                <p className="text-[12px] text-[#8A8A93] mt-1">From {active.user_name || '(no name)'} · {active.user_email}</p>
              </div>
              <button onClick={() => setActive(null)} className="text-[#8A8A93] text-2xl leading-none">×</button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="bg-[var(--surface-2)] rounded-2xl p-4 border border-[var(--hairline)]">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93] mb-1">User</p>
                <p className="text-[13px] text-[#0F0F10] whitespace-pre-wrap">{active.message}</p>
              </div>

              {(active.replies || []).map((r, i) => (
                <div key={i} className="bg-[#FEF2F2] rounded-2xl p-4 border border-[#B91C1C]/15">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-[#B91C1C] mb-1">You · {relativeTime(r.created_at)}</p>
                  <p className="text-[13px] text-[#0F0F10] whitespace-pre-wrap">{r.text}</p>
                </div>
              ))}

              {/* Status quick actions */}
              <div className="flex gap-2 flex-wrap">
                {Object.keys(STATUS_CFG).filter(s => s !== active.status).map(s => (
                  <button key={s} onClick={() => updateTicket({ status: s })} disabled={acting}
                    data-testid={`admin-set-status-${s}`}
                    className="px-3 py-1.5 rounded-full text-[11px] font-semibold border border-[var(--hairline-strong)] hover:bg-black/[0.02] disabled:opacity-50 transition">
                    Mark {STATUS_CFG[s].label}
                  </button>
                ))}
              </div>

              {/* Reply composer */}
              <div className="space-y-2">
                <p className="text-[10px] uppercase tracking-wider font-bold text-[#8A8A93]">Reply (emails the user)</p>
                <textarea
                  value={reply}
                  onChange={(e) => setReply(e.target.value.slice(0, 5000))}
                  rows={5}
                  placeholder="Hi! Thanks for reaching out — here's what we did…"
                  data-testid="admin-reply-textarea"
                  className="w-full rounded-xl border border-[var(--hairline-strong)] bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#B91C1C]/30 resize-none"
                />
                <div className="flex items-center justify-between text-[11px] text-[#8A8A93]">
                  <span>{reply.length}/5000</span>
                  <span>Sent via Resend</span>
                </div>
                <button
                  onClick={() => updateTicket({ admin_reply: reply, status: 'in_progress' })}
                  disabled={!reply.trim() || acting}
                  data-testid="admin-send-reply"
                  className="w-full py-3 rounded-full brand-gradient text-white text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-40 brand-glow-sm hover:scale-[1.01] transition-all"
                >
                  {acting ? <Loader2 size={14} className="animate-spin"/> : <Send size={14}/>}
                  Send reply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
